import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, HandHeart, Footprints, HeartHandshake, Network, Megaphone, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { Reveal, LineReveal } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";
import { involveOptions, images, pacerClub, whatsappChannel } from "../content/site";
import { getActivityBySlug } from "../lib/content";

const optionIcons = { HandHeart, Footprints, HeartHandshake, Network, Megaphone, Share2 };

const ACCENTS = {
  animals: { color: "#43D2FF", text: "#0B7A8C", on: "#1C1917" },
  people: { color: "#FF2F89", text: "#D6106A", on: "#F7F5F0" },
  nature: { color: "#FFC300", text: "#8F6D00", on: "#1C1917" },
};

export default function GetInvolved() {
  const [presetCategory, setPresetCategory] = useState(null);
  const formRef = useRef(null);
  const challenge = getActivityBySlug("1-lakh-steps-for-paws");

  const pickOption = (opt) => {
    if (opt.action.type === "share") {
      const shareData = { title: "The PilCoo Foundation", text: "Kindness needs somewhere to go — The PilCoo Foundation", url: window.location.origin };
      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else {
        navigator.clipboard?.writeText(window.location.origin);
        toast.success("Link copied — thank you for spreading the word.");
      }
      return;
    }
    if (opt.action.type === "form") {
      setPresetCategory(opt.action.enquiry);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <main>
      <SEO
        title="Get Involved"
        description="There isn't only one way to help. Volunteer, join an activity, walk with us on Pacer, follow on WhatsApp, or tell us about a need."
        path="/get-involved"
      />

      <section className="pt-32 md:pt-44 pb-16 md:pb-20" data-testid="involved-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Get involved</p>
          <h1 className="mt-6 font-serif tracking-tighter leading-[0.98] text-ink text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
            <LineReveal lines={["There isn't only", "one way to help."]} delay={0.15} />
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-ink-2">
              Giving money is one way to contribute — it has never been the only
              one. Pick the door that fits you; they all lead to the same place.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-24" data-testid="involved-options">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {involveOptions.map((opt, i) => {
            const Icon = optionIcons[opt.icon];
            const accent = ACCENTS[opt.accent];
            const ctaInner = (
              <>
                {opt.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            );
            const ctaCls =
              "group mt-6 inline-flex items-center gap-2 self-start rounded-full px-6 py-3 text-sm font-semibold whitespace-nowrap transition-all duration-300 hover:scale-[1.04] active:scale-95";
            return (
              <Reveal key={opt.key} delay={i * 0.07}>
                <div
                  className="group flex flex-col h-full border rounded-xl p-7 md:p-8 transition-transform duration-500 hover:-translate-y-1"
                  style={{ borderColor: `${accent.color}59`, backgroundColor: `${accent.color}0D` }}
                  data-testid={`involved-option-${opt.key}`}
                >
                  <span
                    className="h-12 w-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${accent.color}2E`, color: accent.text }}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <h2 className="mt-5 font-serif text-2xl tracking-tight text-ink">{opt.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2 grow">{opt.text}</p>
                  {opt.action.type === "link" ? (
                    <Link
                      to={opt.action.to}
                      data-testid={`involved-cta-${opt.key}`}
                      className={ctaCls}
                      style={{ backgroundColor: accent.color, color: accent.on }}
                    >
                      {ctaInner}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => pickOption(opt)}
                      data-testid={`involved-cta-${opt.key}`}
                      className={ctaCls}
                      style={{ backgroundColor: accent.color, color: accent.on }}
                    >
                      {ctaInner}
                    </button>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section ref={formRef} className="py-20 md:py-28 bg-sand-2 border-y border-line scroll-mt-20" data-testid="involved-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">
                Say hello.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-ink-2 max-w-md">
                Tell us who you are and what&rsquo;s on your mind. Every message is
                read personally — no ticket queues, no autoresponders pretending
                to be people.
              </p>
              <div className="mt-10 overflow-hidden rounded-xl aspect-[4/3] max-w-md">
                <img
                  src={images.involve}
                  alt="Volunteers working together outdoors in a park"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <EnquiryForm presetCategory={presetCategory} />
            </Reveal>
          </div>
        </div>
      </section>


      <section className="pb-16 md:pb-20" data-testid="involved-pacer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div
              className="rounded-xl border p-8 md:p-12"
              style={{ borderColor: "#43D2FF73", backgroundColor: "#43D2FF0F" }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "#0B7A8C" }}>
                    Pacer Community
                  </p>
                  <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-ink">
                    Want to start with something simple? <em className="text-ink-2">Walk with PilCoo.</em>
                  </h2>
                  <p className="mt-5 max-w-xl text-sm md:text-base leading-relaxed text-ink-2">
                    Join The PilCoo Foundation community on Pacer. Take part in
                    walking challenges, community goals and movement-led
                    initiatives — free to join and open to everyone.
                  </p>
                  <a
                    href={pacerClub.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Join our Pacer Community (opens in new tab)"
                    data-testid="involved-pacer-cta"
                    className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-300 hover:scale-[1.04] active:scale-95"
                    style={{ backgroundColor: "#43D2FF", color: "#1C1917" }}
                  >
                    Join our Pacer Community
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
                <div className="lg:col-span-5">
                  <div className="rounded-xl border border-line bg-sand p-7 md:p-8" data-testid="involved-challenge">
                    <p className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-ink-2">
                      Happening now
                    </p>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl tracking-tight text-ink">
                      1 Lakh Steps for Paws
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-2">
                      <span className="font-semibold text-ink">120,827 steps and counting</span>{" "}
                      — already beyond our original 1,00,000-step community goal.
                    </p>
                    <a
                      href={challenge.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Join the Challenge on Pacer (opens in new tab)"
                      data-testid="involved-challenge-cta"
                      className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-sand"
                    >
                      Join the Challenge
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                    <p className="mt-4 text-xs text-ink-2/80">
                      The Pacer Club is our permanent community; 1 Lakh Steps for
                      Paws is the current challenge inside it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-24" data-testid="involved-whatsapp">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div
              className="rounded-xl border p-7 md:p-9 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
              style={{ borderColor: "#FF2F8959", backgroundColor: "#FF2F890A" }}
            >
              <span
                className="h-12 w-12 shrink-0 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#FF2F8926", color: "#D6106A" }}
              >
                <MessageCircle className="h-5 w-5" />
              </span>
              <div className="grow">
                <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">
                  Stay close to the kindness.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-ink-2 max-w-2xl">
                  Follow The PilCoo Foundation on WhatsApp for new activities,
                  events, volunteering opportunities, stories and simple ways to
                  participate.
                </p>
              </div>
              <a
                href={whatsappChannel.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow our WhatsApp Channel (opens in new tab)"
                data-testid="involved-whatsapp-cta"
                className="inline-flex items-center gap-2 self-start md:self-auto rounded-full px-6 py-3 text-sm font-semibold whitespace-nowrap transition-all duration-300 hover:scale-[1.04] active:scale-95"
                style={{ backgroundColor: "#FF2F89", color: "#F7F5F0" }}
              >
                Follow our WhatsApp Channel
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
